import {Job, Manifest, OrderRequirement, Pref, Skill, SkillOverrideNoteInput} from "../../generated/graphql";
import {TManifestStopEnhanced} from "../../components/common/types";
import {TenantPreferences} from "../../constants/constants";
import {extractSimplePref} from "../../providers/PreferenceProvider";
import {ASSIGNMENT_SUBMITTED_NOTIFICATION_DEFAULT} from "../../components/settings/CompanySettings/common/constants";

type RequiredItem = Skill | OrderRequirement;

/** Items of `required` whose id is not in `held`, matched by id, in `required` order. */
export const getMissingItems = <T extends {id: number}>(
  required: T[],
  held: Array<{id: number}> | null | undefined
): T[] => {
  const heldIds = new Set((held ?? []).map((item) => item.id));
  return required.filter((item) => !heldIds.has(item.id));
};

/** The job's skills that count: only those still active in the tenant's skill list. */
export const getActiveOrderSkills = (orderSkills: Skill[] | null | undefined, activeSkills: Skill[]): Skill[] => {
  const activeSkillIds = new Set(activeSkills.map((skill) => skill.id));
  return (orderSkills ?? []).filter((skill) => activeSkillIds.has(skill.id));
};

/**
 * What a job requires from a driver and which of it the driver lacks.
 *
 * Skills mode compares the job's active skills with `driver.skills`; otherwise the
 * job's requirements are compared with `driver.qualifications` (CMS "Driver Require").
 * The two lists never satisfy each other, even for items with the same name.
 */
export const getRequirementGap = ({
  order,
  driver,
  isSkillsBasedRequirements,
  activeSkills
}: {
  order?: {orderSkills?: Skill[] | null; requirements?: OrderRequirement[] | null} | null;
  driver?: {skills?: Array<{id: number}> | null; qualifications?: Array<{id: number}> | null} | null;
  isSkillsBasedRequirements: boolean;
  activeSkills: Skill[];
}): {required: RequiredItem[]; missing: RequiredItem[]} => {
  const required: RequiredItem[] = isSkillsBasedRequirements
    ? getActiveOrderSkills(order?.orderSkills, activeSkills)
    : order?.requirements ?? [];
  const held = isSkillsBasedRequirements ? driver?.skills : driver?.qualifications;
  return {required, missing: getMissingItems(required, held)};
};

export const getMatchRequirementManifests = ({
  selectedRows,
  manifests,
  isSkillsBasedRequirements,
  activeSkills
}: {
  selectedRows: Job[] | TManifestStopEnhanced[];
  manifests?: Manifest[];
  isSkillsBasedRequirements: boolean;
  activeSkills: Skill[];
}) => {
  if (!manifests || selectedRows.length === 0) return [];

  return manifests.filter((manifest) =>
    selectedRows.every(
      (row) =>
        getRequirementGap({order: row.order, driver: manifest.driver, isSkillsBasedRequirements, activeSkills}).missing
          .length === 0
    )
  );
};

const MAX_NOTE_LENGTH = 255;
const MIN_REASON_LENGTH = 50;

/**
 * Truncate a skill name list so the prefix ("Missing: X, Y | ") stays within
 * a budget, leaving at least {@link MIN_REASON_LENGTH} chars for the reason.
 *
 * When all names don't fit, keeps as many as possible and appends "+N more".
 */
export const truncateSkillList = (skillNames: string[], maxPrefixLength: number): string => {
  const full = skillNames.join(", ");
  if (`Missing: ${full} | `.length <= maxPrefixLength) return full;

  let result = "";
  let included = 0;
  for (const name of skillNames) {
    const remaining = skillNames.length - included - 1;
    const suffix = remaining > 0 ? `, +${remaining} more` : "";
    const candidate = included === 0 ? name : `${result}, ${name}`;
    if (`Missing: ${candidate}${suffix} | `.length > maxPrefixLength && included > 0) {
      return `${result}, +${skillNames.length - included} more`;
    }
    result = candidate;
    included++;
  }
  return result;
};

/**
 * Build SkillOverrideNoteInput[] from per-job override reasons.
 *
 * For each job whose orderId has a reason, computes the skills the driver is
 * missing and formats the note as "Missing: SKILL1, SKILL2 | <reason text>".
 * When the skill list is too long, it truncates to "SKILL1, +N more" so the
 * total note fits within {@link MAX_NOTE_LENGTH}.
 */
export const buildSkillOverrideNotes = ({
  jobs,
  reasons,
  driverSkills,
  activeSkills
}: {
  jobs: Array<{jobId: number; order?: {orderId?: number | null; orderSkills?: Skill[] | null} | null}>;
  reasons: Record<number, string>;
  driverSkills: Array<{id: number}> | null | undefined;
  activeSkills: Skill[];
}): SkillOverrideNoteInput[] => {
  const notes: SkillOverrideNoteInput[] = [];
  const maxPrefixLength = MAX_NOTE_LENGTH - MIN_REASON_LENGTH;

  for (const job of jobs) {
    const reason = reasons[job.jobId];
    if (!reason?.trim() || !job.order?.orderId) continue;

    const missingSkillNames = getMissingItems(
      getActiveOrderSkills(job.order.orderSkills, activeSkills),
      driverSkills
    ).map((s) => s.name);

    if (missingSkillNames.length === 0) continue;

    const skillList = truncateSkillList(missingSkillNames, maxPrefixLength);
    const note = `Missing: ${skillList} | ${reason.trim()}`;
    notes.push({orderId: job.order.orderId, note});
  }

  return notes;
};

/**
 * Build SkillOverrideNoteInput[] from per-job override reasons for stops
 * (used during reassignment).
 */
export const buildSkillOverrideNotesFromStops = ({
  stops,
  reasons,
  driverSkills,
  activeSkills
}: {
  stops: TManifestStopEnhanced[];
  reasons: Record<number, string>;
  driverSkills: Skill[] | null | undefined;
  activeSkills: Skill[];
}): SkillOverrideNoteInput[] => {
  // Deduplicate stops by jobId to avoid duplicate notes for the same order
  const uniqueJobs = Array.from(new Map(stops.map((s) => [s.job.jobId, s])).values());

  return buildSkillOverrideNotes({
    jobs: uniqueJobs.map((s) => ({
      jobId: s.job.jobId,
      order: s.order ? {orderId: s.order.orderId, orderSkills: s.order.orderSkills} : undefined
    })),
    reasons,
    driverSkills,
    activeSkills
  });
};

/** Whether the "Assignment Submitted" toast should show after jobs are assigned to a driver. */
export const isAssignmentSubmittedNotificationEnabled = (prefs: Pref[] | null | undefined): boolean =>
  extractSimplePref<boolean>(
    prefs ?? [],
    TenantPreferences.assignmentSubmittedNotification,
    ASSIGNMENT_SUBMITTED_NOTIFICATION_DEFAULT
  ).value !== false;
